import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild
} from '@angular/core';
import { FormArray } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isEmpty } from 'lodash';
import { Subscription, concatMap, forkJoin, from, of } from 'rxjs';
import { CompanyForm } from 'src/app/classes/form-groups/company-form';
import { CompanyVendorForm } from 'src/app/classes/form-groups/company-vendor-form';
import { SmallBusinessOptionForm } from 'src/app/classes/form-groups/small-business-option-form';
import { VendorForm } from 'src/app/classes/form-groups/vendor-form';
import { VendorOperatingStatesForm } from 'src/app/classes/form-groups/vendor-operating-states-form';
import {
  ClientRegisteredVendor,
  CompanyOffice,
  ExemptVendor,
  Vendor
} from 'src/app/classes/models';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { State } from 'src/app/interfaces/state';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { AddressService } from 'src/app/services/address.service';
import { AuthService } from 'src/app/services/auth.service';
import { ClientRegisteredVendorService } from 'src/app/services/client-registered-vendor.service';
import { CompanyVendorOfficeService } from 'src/app/services/company-vendor-office.service';
import { ExemptVendorService } from 'src/app/services/exempt-vendor.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SmallBusinessOptionService } from 'src/app/services/small-business-option.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorTradeStateService } from 'src/app/services/vendor-trade-state.service';
import { VendorService } from 'src/app/services/vendor.service';
import { VendorEditComponent } from 'src/app/shared-components/vendor-edit/vendor-edit.component';
import { vendorsService } from 'src/app/shared/vive-api/vendors.service';
import { AddressDto, VendorDto } from 'src/app/shared/vive-api/vive-api-models';
import {
  isLegalNameLockedForUser,
  vendorNameLengthModalInputs
} from 'src/app/validators/vendor-name-length-validator';

@Component({
  selector: 'app-admin-vendor-info',
  templateUrl: './admin-vendor-info.component.html',
  styleUrls: ['./admin-vendor-info.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminVendorInfoComponent {
  private resignOriginalValues: Partial<
    Pick<Vendor, 'legalName' | 'dba' | 'companyType' | 'llcType'>
  > = {};

  constructor(
    private SharedDataService: SharedDataService,
    private VendorService: VendorService,
    private ExemptVendorService: ExemptVendorService,
    private ClientRegisteredVendorService: ClientRegisteredVendorService,
    private AddressService: AddressService,
    private SmallBusinessOptionService: SmallBusinessOptionService,
    private UtilityService: UtilityService,
    private ToastService: ToastService,
    private CompanyVendorOfficeService: CompanyVendorOfficeService,
    private VendorTradeStateService: VendorTradeStateService,
    private ChangeDetectorRef: ChangeDetectorRef,
    private ModalService: NgbModal,
    private AuthService: AuthService
  ) {
    this.VendorFormGroup.setControl(
      'CompanyVendors',
      new FormArray<CompanyVendorForm>([])
    );

    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent<number>().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetVendorId' && typeof data === 'number') {
            this.VendorId = data;

            this.VendorService.getVendorV2(this.VendorId, {
              excludedAttributes: JSON.stringify(['logo']),
              StreetAddress: JSON.stringify({}),
              MailingAddress: JSON.stringify({}),
              RemitToAddress: JSON.stringify({}),
              SmallBusinessOption: JSON.stringify({
                options: { required: false },
                attributes: ['id', 'VendorId', 'type']
              }),
              CompanyVendor: JSON.stringify({
                attributes: ['id', 'CompanyId', 'VendorId'],
                Company: {
                  attributes: ['id', 'legalName'],
                  CompanyOffice: {}
                },
                CompanyVendorOffice: {
                  CompanyOffice: {}
                }
              })
            }).subscribe({
              next: (Vendor: Vendor) => {
                const {
                  legalName,
                  country,
                  StreetAddress,
                  MailingAddress,
                  RemitToAddress,
                  SmallBusinessOptions,
                  CompanyVendors
                } = Vendor;

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Vendor legal name.',
                    { returnError: true }
                  );
                }

                if (!country) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing country information.',
                    { returnError: true }
                  );
                }

                if (!CompanyVendors) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client/Vendor relationships.',
                    { returnError: true }
                  );
                }

                if (SmallBusinessOptions) {
                  SmallBusinessOptions.forEach((sbo) => {
                    const SmallBusinessOptionFormGroup =
                      new SmallBusinessOptionForm();
                    SmallBusinessOptionFormGroup.patchValue(sbo);
                    this.selectedSmallBusinessOptionsFormArray.push(
                      SmallBusinessOptionFormGroup
                    );
                  });
                }

                CompanyVendors.forEach((CompanyVendor) => {
                  const { CompanyId, Company, CompanyVendorOffices } =
                    CompanyVendor;

                  if (!CompanyId) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Missing Client Id.',
                      { returnError: true }
                    );
                  }

                  if (!Company) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'Missing Client information.',
                      { returnError: true }
                    );
                  }

                  const CompanyVendorFormGroup = new CompanyVendorForm();
                  const CompanyFormGroup = new CompanyForm();

                  const RequiredCompanyVendorFormControls = new Set([
                    'id',
                    'CompanyId',
                    'VendorId',
                    'CompanyVendorOffices'
                  ]);
                  Object.keys(CompanyVendorFormGroup.controls).forEach(
                    (controlField) => {
                      if (
                        !RequiredCompanyVendorFormControls.has(controlField)
                      ) {
                        CompanyVendorFormGroup.removeControl(controlField);
                      }
                    }
                  );

                  const RequiredCompanyFormControls = new Set([
                    'id',
                    'legalName',
                    'CompanyOffices'
                  ]);
                  Object.keys(CompanyFormGroup.controls).forEach(
                    (controlField) => {
                      if (!RequiredCompanyFormControls.has(controlField)) {
                        CompanyFormGroup.removeControl(controlField);
                      }
                    }
                  );

                  CompanyFormGroup.patchValue(Company);
                  CompanyVendorFormGroup.setControl(
                    'Company',
                    CompanyFormGroup
                  );
                  CompanyVendorFormGroup.patchValue(CompanyVendor);

                  const { CompanyOffices } = Company;
                  if (CompanyOffices) {
                    this.CompanyIdToCompanyOfficesMap.set(
                      CompanyId,
                      CompanyOffices
                    );
                  }

                  this.ClientCompanyVendorMap.set(
                    CompanyId,
                    CompanyVendorFormGroup
                  );
                  this.VendorFormGroup.controls['CompanyVendors']!.push(
                    CompanyVendorFormGroup
                  );
                });

                this.LocalizationInfo =
                  this.UtilityService.getLocalizedValuesMap(country);

                Object.assign(this, {
                  StreetAddress,
                  MailingAddress,
                  RemitToAddress
                });

                this.VendorFormGroup.patchValue(Vendor);

                this.resignOriginalValues = {
                  legalName: Vendor.legalName,
                  dba: Vendor.dba,
                  companyType: Vendor.companyType,
                  llcType: Vendor.llcType
                };

                const {
                  StreetAddress: StreetAddressFormGroup,
                  MailingAddress: MailingAddressFormGroup,
                  RemitToAddress: RemitToAddressFormGroup
                } = this.VendorFormGroup.controls;

                if (this.StreetAddress) {
                  const { address1, address2, city, state, zip } =
                    this.StreetAddress;
                  StreetAddressFormGroup.patchValue({
                    VendorId: Vendor.id,
                    ...(address1 && { address1 }),
                    ...(address2 && { address2 }),
                    ...(city && { city }),
                    ...(state && { state: state as State }),
                    ...(zip && { zip }),
                    ...(country && { country })
                  });
                }

                if (
                  this.MailingAddress &&
                  this.MailingAddress.id !== this.StreetAddress?.id
                ) {
                  const { address1, address2, city, state, zip } =
                    this.MailingAddress;
                  MailingAddressFormGroup.patchValue({
                    VendorId: Vendor.id,
                    ...(address1 && { address1 }),
                    ...(address2 && { address2 }),
                    ...(city && { city }),
                    ...(state && { state: state as State }),
                    ...(zip && { zip }),
                    ...(country && { country })
                  });
                } else if (this.StreetAddress) {
                  StreetAddressFormGroup.patchValue({ isMailing: true });
                  MailingAddressFormGroup.patchValue({ isMailing: false });
                }

                if (
                  this.RemitToAddress &&
                  this.RemitToAddress.id !== this.StreetAddress?.id &&
                  this.RemitToAddress.id !== this.MailingAddress?.id
                ) {
                  const { address1, address2, city, state, zip } =
                    this.RemitToAddress;
                  RemitToAddressFormGroup.patchValue({
                    VendorId: Vendor.id,
                    ...(address1 && { address1 }),
                    ...(address2 && { address2 }),
                    ...(city && { city }),
                    ...(state && { state: state as State }),
                    ...(zip && { zip }),
                    ...(country && { country })
                  });
                } else if (this.StreetAddress && !this.MailingAddress) {
                  StreetAddressFormGroup.patchValue({ isRemitTo: true });
                  RemitToAddressFormGroup.patchValue({ isRemitTo: false });
                } else if (this.MailingAddress) {
                  MailingAddressFormGroup.patchValue({ isRemitTo: true });
                  RemitToAddressFormGroup.patchValue({ isRemitTo: false });
                }

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Vendor - ${legalName}`
                );
                this.ChangeDetectorRef.markForCheck();
              }
            });
          }

          if (eventName === 'SetExemptVendorId' && typeof data === 'number') {
            this.ExemptVendorId = data;

            this.ExemptVendorService.getExemptVendor(
              this.UtilityService.stringifyObjectsInQueryObject({
                id: this.ExemptVendorId,
                MailingAddress: {},
                StreetAddress: {},
                Contact: {
                  StreetAddress: {}
                },
                CompanyVendor: {
                  attributes: ['id', 'CompanyId'],
                  Company: {
                    attributes: ['id', 'country']
                  }
                }
              })
            ).subscribe({
              next: (ExemptVendor: ExemptVendor) => {
                this.ExemptVendor = ExemptVendor;
                const { legalName } = this.ExemptVendor;

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Exempt Vendor legal name.',
                    { returnError: true }
                  );
                }

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Compliance Exempt Vendor - ${legalName}`
                );
                this.ChangeDetectorRef.markForCheck();
              }
            });
          }

          if (
            eventName === 'SetClientRegisteredVendorId' &&
            typeof data === 'number'
          ) {
            this.ClientRegisteredVendorId = data;

            this.ClientRegisteredVendorService.getClientRegisteredVendor(
              this.UtilityService.stringifyObjectsInQueryObject({
                id: this.ClientRegisteredVendorId,
                MailingAddress: {},
                StreetAddress: {},
                CompanyVendor: {
                  attributes: ['id', 'CompanyId', 'CompanyComplianceTypeId'],
                  Company: {
                    attributes: ['id', 'country']
                  }
                }
              })
            ).subscribe({
              next: (ClientRegisteredVendor: ClientRegisteredVendor) => {
                this.ClientRegisteredVendor = ClientRegisteredVendor;

                const { legalName } = this.ClientRegisteredVendor;

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Missing Client Registered Vendor legal name.',
                    { returnError: true }
                  );
                }

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Client Registered Vendor - ${legalName}`
                );
                this.ChangeDetectorRef.markForCheck();
              }
            });
          }
        }
      });

    this.SharedDataService.sendEvent('GetVendorId');
  }

  SharedDataServiceSubscription: Subscription;
  VendorId?: number;
  ExemptVendorId?: number;
  ClientRegisteredVendorId?: number;

  ExemptVendor?: ExemptVendor;
  ClientRegisteredVendor?: ClientRegisteredVendor;

  LocalizationInfo!: LocalizationInfo;
  VendorFormGroup = new VendorForm();

  StreetAddress?: AddressDto;
  MailingAddress?: AddressDto;
  RemitToAddress?: AddressDto;

  OperatingStatesForm = new VendorOperatingStatesForm();
  selectedSmallBusinessOptionsFormArray =
    new FormArray<SmallBusinessOptionForm>([]);
  CompanyIdToCompanyOfficesMap = new Map<number, CompanyOffice[]>([]);

  // Maps CompanyId to the CompanyVendor record.
  // There should only ever be 1 CompanyVendor record per Client per Vendor.
  ClientCompanyVendorMap = new Map<number, CompanyVendorForm>();

  // The editor loads the agreement and multi-client flags that lock the legal
  // name; this component's own vendor query does not select them.
  @ViewChild(VendorEditComponent) VendorEdit?: VendorEditComponent;

  save(resignConfirmed = false, operatingStatesConfirmed = false) {
    if (!this.VendorId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
        returnError: true
      });
    }

    const errors = this.UtilityService.generateValidationErrorsObject(
      this.VendorFormGroup
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error with a field below.',
        { returnError: true }
      );
    }

    const role = this.AuthService.getUser().UserAccountRole?.role;
    // saveVendor discards everything but qtyEmployees for the insurance team,
    // so a name they submit is never written and must not block them.
    if (role !== 'ADMIN_INSURANCE_TEAM') {
      const { legalName, dba } = this.VendorFormGroup.getRawValue();
      const modalInputs = vendorNameLengthModalInputs(
        { legalName, dba },
        {
          legalNameLockedForUser: isLegalNameLockedForUser(
            this.VendorEdit?.Vendor,
            role
          )
        }
      );
      if (modalInputs) {
        const { componentInstance } = this.ModalService.open(
          ConfirmModalComponent,
          { centered: true }
        );
        Object.assign(componentInstance, modalInputs);
        return;
      }
    }

    if (!resignConfirmed) {
      const updated = this.VendorFormGroup.getRawValue() as Vendor;
      const willRequireResign = (
        ['legalName', 'dba', 'companyType', 'llcType'] as const
      ).some(
        (field) =>
          (this.resignOriginalValues[field] ?? null) !==
          (updated[field] ?? null)
      );

      if (willRequireResign) {
        const { componentInstance, closed } = this.ModalService.open(
          ConfirmModalComponent,
          { backdrop: 'static', modalDialogClass: 'confirm-modal' }
        );
        Object.assign(componentInstance, {
          title: 'Vendor will need to re-sign',
          displayText:
            'Changing the legal name, DBA, or tax classification will require this vendor to re-sign their Vendor Service Agreement and submit a new W-9 with all of their active clients. Their existing signed documents will be archived until they re-sign. Continue?',
          continueButtonText: 'Save & require re-sign'
        });
        closed.subscribe((proceed: boolean) => {
          if (proceed) {
            this.save(true);
          }
        });
        return;
      }
    }

    if (!operatingStatesConfirmed && this.OperatingStatesForm.dirty) {
      this.VendorTradeStateService.confirmOperatingStateChanges(
        this.OperatingStatesForm
      ).subscribe((proceed: boolean) => {
        if (proceed) {
          this.save(true, true);
        }
      });
      return;
    }

    const {
      MailingAddress: MailingAddressFormGroup,
      StreetAddress: StreetAddressFormGroup,
      RemitToAddress: RemitToAddressFormGroup,
      CompanyVendors: CompanyVendorsFormArray
    } = this.VendorFormGroup.controls;

    const observables = [
      this.VendorService.saveVendor(
        this.VendorFormGroup.getRawValue() as Vendor
      )
    ];

    if (
      !MailingAddressFormGroup.controls['isMailing'].value &&
      this.MailingAddress &&
      this.MailingAddress.id !== this.StreetAddress?.id
    ) {
      if (!this.MailingAddress.id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Mailing Address is missing id.',
          { returnError: true }
        );
      }
      observables.push(
        this.AddressService.deleteAddress(this.MailingAddress.id)
      );
    }

    if (
      !RemitToAddressFormGroup.controls['isRemitTo'].value &&
      this.RemitToAddress &&
      this.RemitToAddress.id !== this.MailingAddress?.id
    ) {
      if (!this.RemitToAddress.id) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Mailing Address is missing id.',
          { returnError: true }
        );
      }
      observables.push(
        this.AddressService.deleteAddress(this.RemitToAddress.id)
      );
    }

    if (this.selectedSmallBusinessOptionsFormArray.dirty) {
      observables.push(
        this.SmallBusinessOptionService.saveSmallBusinessOptions(
          this.VendorId,
          this.selectedSmallBusinessOptionsFormArray.getRawValue()
        )
      );
    }

    const companyVendorOfficeObservables =
      this.CompanyVendorOfficeService.saveDirtySelections(
        CompanyVendorsFormArray!,
        this.VendorId!
      );

    // Make sure this forkJoin finishes correctly. Need both observables to emit 'complete' event.
    if (companyVendorOfficeObservables.length > 0) {
      observables.push(forkJoin(companyVendorOfficeObservables));
    }

    // Runs after the Client Offices save so it builds on any states that
    // save added.
    const save$ = forkJoin(observables).pipe(
      concatMap(() =>
        this.OperatingStatesForm.dirty
          ? this.VendorTradeStateService.saveOperatingStates(
              this.VendorId!,
              this.OperatingStatesForm
            )
          : of(null)
      )
    );

    // The addresses save one at a time, each after the previous one resolves.
    save$
      .pipe(
        concatMap(() =>
          from(
            vendorsService.updateOne({
              pathParams: { vendorId: this.VendorId! },
              data: { StreetAddress: StreetAddressFormGroup.getRawValue() }
            })
          )
        ),
        concatMap(({ StreetAddress }) => {
          this.StreetAddress = StreetAddress;

          return MailingAddressFormGroup.controls['isMailing'].value
            ? from(
                vendorsService.updateOne({
                  pathParams: { vendorId: this.VendorId! },
                  data: {
                    MailingAddress: MailingAddressFormGroup.getRawValue()
                  }
                })
              )
            : of<Partial<VendorDto>>({});
        }),
        concatMap(({ MailingAddress }) => {
          if (!isEmpty(MailingAddress)) {
            this.MailingAddress = MailingAddress;
          }

          return RemitToAddressFormGroup.controls['isRemitTo'].value
            ? from(
                vendorsService.updateOne({
                  pathParams: { vendorId: this.VendorId! },
                  data: {
                    MailingAddress: RemitToAddressFormGroup.getRawValue()
                  }
                })
              )
            : of<Partial<VendorDto>>({});
        })
      )
      .subscribe({
        next: ({ RemitToAddress }) => {
          if (!isEmpty(RemitToAddress)) {
            this.RemitToAddress = RemitToAddress;
          }

          this.ToastService.addSuccessToast(
            'Success',
            'Successfully saved Vendor information.'
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.SharedDataServiceSubscription.unsubscribe();
  }
}
